import getConfig from 'next/config';

const { NEXT_PUBLIC_API_URL: canonicalUrl } = getConfig().publicRuntimeConfig;

let position: number;
let bc: any;

function getBreadCrumbs(blocks) {
  return blocks?.find((block: any) => block?.component?.name === 'BreadCrumbs')?.component.options;
}

export function addLocaleToBreadCrumbs(breadcrumbSchema: any, locale: string | undefined) {
  if (!locale && locale === 'en-us') return breadcrumbSchema;
  const breadcrumbSchemaWithLocale = breadcrumbSchema.itemListElement.map((item: any) => ({
    ...item,
    item: item.item.replace('com/', `com/${locale}/`),
  }));
  return breadcrumbSchemaWithLocale;
}

export function breadCrumbsData(blocks: any, locale: string | undefined) {
  const data = getBreadCrumbs(blocks);
  if (!data) return null;

  position = 1;
  bc = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [],
  };

  addCrumb('Home', locale);

  data.crumbs?.forEach(crumb => {
    addCrumb(crumb.pageName, locale, crumb.uri);
  });

  addCrumb(data.currentPageName, locale, data.currentUri);
  return JSON.stringify(bc);
}

function addCrumb(name: string, locale: string | undefined, url?: string) {
  if (url) url = `${url?.replaceAll(/^\/|\/$/gi, '')}/`;
  bc.itemListElement.push({
    '@type': 'ListItem',
    position,
    name,
    item: `${canonicalUrl}/${locale && locale !== 'en-us' ? `${locale}/` : ''}${url || ''}`,
  });
  position += 1;
}

export function getFaqSchema(items: { question: { content: string }; answer: string }[] = []) {
  if (items.length === 0) {
    return '';
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
  };

  const entities = items.map((item: { question: { content: string }; answer: string }) => {
    const textWithoutTags =
      item.answer
        ?.replace(/<[^]+?>/g, ' ')
        ?.replace(/\s+/g, ' ')
        ?.trim() || '';

    return {
      '@type': 'Question',
      name: item.question.content,
      acceptedAnswer: {
        '@type': 'Answer',
        text: textWithoutTags,
      },
    };
  });

  schema['mainEntity'] = entities;

  return JSON.stringify(schema, null, 2);
}

export function addOrganizationJsonLd(phone: string, country: string, url: string) {
  return {
    __html: `{
    "@context": "https://schema.org/",
    "@type": "Organization",
    "name": "Brilliant Earth",
    "url": "${url}",
    "logo": "https://css.brilliantearth.com/static/img/logo-new.svg",
    "foundingDate": "2005",
    "founder": [{"@type": "Person", "name": "Beth Gerstein"}, {"@type": "Person", "name": "Eric Grossberg"}],
    "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "${phone}",
        "contactType": "customer service"
    },
    "sameAs": [
    	"https://www.facebook.com/brilliantearth",
      "https://twitter.com/BrilliantEarth",
      "https://www.instagram.com/brilliantearth/",
      "https://www.youtube.com/@BrilliantEarthInc",
      "https://www.linkedin.com/company/brilliantearth",
      "https://www.pinterest.com/brilliantearth/",
      "https://en.wikipedia.org/wiki/Brilliant_Earth",
      "https://www.tiktok.com/@brilliantearth"
    ]
    }
  `,
  };
}

export function addPageJsonLd(title: string, url: string, description: string, locale: string | undefined) {
  return {
    __html: `{
    "@context": "https://schema.org/",
    "@type": "WebPage",
    "@id": "${url}#webpage",
    "url": "${url}",
    "name": "${title}",
    "description": "${description}",
    "isPartOf": "${canonicalUrl}${locale && locale !== 'en-us' ? `/${locale}` : ''}/#website "
  }
  `,
  };
}

export function addBreadcrumbJsonLd(breadcrumbs: string | null) {
  return {
    __html: breadcrumbs || '',
  };
}

export function addHomeJsonLd(url: string) {
  return {
    __html: `{
      "@context" : "https://schema.org",
      "@type" : "WebSite",
      "name" : "Brilliant Earth",
      "url" : "${url}"
    }
  `,
  };
}

export function addArticleJsonLd(
  url: string,
  title: string,
  description: string,
  datePublished: string,
  locationImage: string
) {
  return {
    __html: `{
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": "${url}#article",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "${url}"
      },
      "headline": "${title}",
      "description": "${description}",
      "url": "${url}",
      ${
        locationImage
          ? `"image": [
        {
          "@type": "ImageObject",
          "url": "https://image.brilliantearth.com/cdn-cgi/image/width=1200,height=1200,quality=100,format=auto/${locationImage}"
        },
        {
          "@type": "ImageObject",
          "url": "https://image.brilliantearth.com/cdn-cgi/image/width=1200,height=900,quality=100,format=auto/${locationImage}"
        },
        {
          "@type": "ImageObject",
          "url": "https://image.brilliantearth.com/cdn-cgi/image/width=1200,height=675,quality=100,format=auto/${locationImage}"
        }
      ],`
          : ''
      }
      "publisher": {
        "@type": "Organization",
        "name": "Brilliant Earth",
        "logo": {
          "@type": "ImageObject",
          "url": "https://css.brilliantearth.com/static/img/logo-new.svg"
        }
      },
      "author": {
        "@type": "Organization",
        "name": "Brilliant Earth"
      },
      "datePublished": "${datePublished}"
    }
  `,
  };
}

// interface IDataShowroom {
//   additionalAddressDescription: string;
//   addressLine1: string;
//   addressLine2: string;
//   city: string;
//   displayName1: string;
//   displayName2: string;
//   googlePlacesId: string;
//   hours: string;
//   latitude: string;
//   longitude: string;
//   locationImage: string;
//   phoneNumber: string;
//   seoSchemaAreaServed: string;
//   seoSchemaDescription: string;
//   setsterLocationId: string;
//   state: string;
//   stateDropdownValue: string;
//   storeDescriptionText: string;
//   storePageLink: string;
//   zipCode: number;
// }

function getStoreInfoBlockData(blocks) {
  const dataExists =
    blocks?.find((block: any) => block?.component?.name === 'StoreInfoBlock')?.component?.options?.store?.value
      ?.data !== undefined;
  if (dataExists) {
    return blocks?.find((block: any) => block?.component?.name === 'StoreInfoBlock')?.component.options.store.value
      .data;
  }
  return null;
}

export function getStoreInfoBlockSchema(blocks: any, asPath?: string) {
  const store = getStoreInfoBlockData(blocks);
  if (!store || store?.displayName1.toLocaleLowerCase() === 'virtual appointment') {
    return '';
  }

  let openingHours = '';
  const hoursItems = store.hours.replaceAll('<p>', '').split('\n');

  if (hoursItems.length > 1) {
    openingHours = store.hours.replaceAll('<p>', '').replaceAll('</p>', ';').split('\n').join(' ');
    openingHours = openingHours.substring(0, openingHours.length - 2);
  } else {
    openingHours = store.hours.replaceAll('<p>', '').replaceAll('</p>', '');
  }

  const areaSchema = store.seoSchemaAreaServed.split(',').map((item: string) => item.trim());

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'JewelryStore',
    image: store.locationImage,
    priceRange: '$$$',
    name: 'Brilliant Earth',
    url: `https://www.brilliantearth.com${asPath}`,
    currenciesAccepted: 'USD',
    address: {
      '@type': 'PostalAddress',
      streetAddress: `${store.addressLine1}${store.addressLine2 && `, ${store.addressLine2}`}`,
      addressLocality: store.city,
      addressRegion: store.state,
      postalCode: `${store.zipCode}`,
    },
    brand: 'Brilliant Earth',
    legalName: 'Brilliant Earth, LLC',
    logo: '/static/img/logo-new.svg',
    parentOrganization: 'Brilliant Earth',
    telephone: store.phoneNumber,
    areaServed: areaSchema,
    openingHoursSpecification: openingHours,
    description: store.seoSchemaDescription,
    sameAs: ['https://www.brilliantearth.com'],
    paymentAccepted: 'cash, credit card, paypal, bank wire',
  };

  return JSON.stringify(schema, null, 2);
}
